import React, { useEffect, useMemo } from 'react';
import styles from './Subscription.module.scss';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'app/store';
import { getSubscriptionInfoByOrganozationId } from 'features/billing/subscription/subscriptionActions';
import { selectInfoUserSelector } from 'features/user/userSlice';
import PageTabs, { IPageTab } from 'components/shared/PageTabs/PageTabs';
import { Outlet } from 'react-router-dom';
import { debounce } from 'lodash';
import {
  isLoadingSubscriptionInfoSelector,
  subscriptionInfoSelector
} from 'features/billing/subscription/subscriptionSlice';
import Preloader from 'components/shared/Preloader';
import { getPrices } from 'features/billing/prices/pricesActions';
import { getCustomerByOrganizationId } from 'features/billing/customer/customerActions';
import { isLoadingCustomerInfoSelector } from 'features/billing/customer/customerSlice';
import { isLoadingPricesSelector, pricesSelector } from 'features/billing/prices/pricesSlice';
import { ISubscription, SubscriptionStatus } from 'features/billing/subscription/subscriptionTypes';
import {
  billingFormsCustomerInfoSelector,
  billingFormsSubscriptionInfoSelector
} from 'features/billing/formStates/billingFormsInfo/billingFormsInfoSlice';
import { CustomerFormInfo, SubscriptionFormInfo } from 'features/billing/formStates/billingFormsInfo/billingFormsInfoTypes';
import { getUpcomingInvoice, getUpcomingInvoiceForSubscription } from 'features/billing/upcomingInvoice/upcomingInvoiceActions';
import { getCurrencies } from 'features/currencies/currenciesActions';
import { getMembers } from 'features/members/membersActions';

const Subscription: React.FC = () => {

  const dispatch = useAppDispatch();
  const userInfo = useSelector(selectInfoUserSelector);
  const subscriptionInfo = useSelector(subscriptionInfoSelector);
  const prices = useSelector(pricesSelector)

  const subscriptionIsLoading = useSelector(isLoadingSubscriptionInfoSelector);
  const customerIsLoading = useSelector(isLoadingCustomerInfoSelector);
  const pricesAreLoading = useSelector(isLoadingPricesSelector);

  const customerFormInfo = useSelector(billingFormsCustomerInfoSelector);
  const subscriptionFormInfo = useSelector(billingFormsSubscriptionInfoSelector);

  const tabsData: IPageTab[] = [
    {
      path: `/subscription`,
      name: 'Billing',
    },
    subscriptionInfo?.status === SubscriptionStatus.Active ? {
      path: `/subscription/invoices`,
      name: 'Invoices',
    } : null,
    {
      path: `/subscription/plans`,
      name: 'Plans',
    },
  ].filter(tab => tab !== null) as IPageTab[];

  useEffect(() => {
    if (userInfo?.organizationId) {
      dispatch(getMembers())
      dispatch(getSubscriptionInfoByOrganozationId(userInfo.organizationId));
      dispatch(getCurrencies())
      dispatch(getCustomerByOrganizationId(userInfo.organizationId));
      dispatch(getPrices());
    }
  }, [userInfo]);

  const debouncedUpcomingInvoiceFetch = useMemo(
    () => debounce((customerFormInfo: CustomerFormInfo, subscriptionFormInfo: SubscriptionFormInfo, subscriptionInfo: ISubscription | null) => {
      if (!subscriptionInfo) return;

      const interval = subscriptionFormInfo?.interval ?? subscriptionInfo!.interval;
      const currency = subscriptionFormInfo?.currency ?? subscriptionInfo!.currency;
      const productId = subscriptionFormInfo?.productId ?? subscriptionInfo!.productId;
      const quantity = subscriptionFormInfo?.quantity ?? subscriptionInfo!.quantity;
      const couponId = subscriptionFormInfo?.couponId ?? subscriptionInfo!.couponId;
      const priceId = prices.find((price) =>
        price.interval === interval &&
        price.productId === productId &&
        price.currency === currency
      )?.id ?? subscriptionInfo!.priceId

      const subscriptionIsActive = subscriptionInfo?.status === SubscriptionStatus.Active;
      const subscriptionIsTrialing = subscriptionInfo?.status === SubscriptionStatus.Trialing;
      const isActiveOrTrialing = subscriptionIsActive || subscriptionIsTrialing;

      if (!isActiveOrTrialing) {
        dispatch(getUpcomingInvoice({
          quantity: quantity,
          priceId: priceId,
          couponId: couponId,
          address: customerFormInfo.address,
          taxIds: Object.entries(customerFormInfo.taxIds).map(([type, id]) => ({ type, id })),
        }));
      }
      else {
        if (interval !== subscriptionInfo!.interval || quantity !== subscriptionInfo!.quantity) {
          dispatch(getUpcomingInvoiceForSubscription({
            subscriptionId: subscriptionInfo!.id,
            quantity: quantity,
            priceId: priceId,
          }));
        }
      }
    }, 500),
    [dispatch]
  );

  useEffect(() => {
    debouncedUpcomingInvoiceFetch(customerFormInfo, subscriptionFormInfo, subscriptionInfo);
    return () => debouncedUpcomingInvoiceFetch.cancel();
  }, [customerFormInfo, subscriptionFormInfo, debouncedUpcomingInvoiceFetch]);

  if (!subscriptionInfo || subscriptionIsLoading || customerIsLoading || pricesAreLoading) {
    return <Preloader />
  }

  return (
    <div className={styles.billing}>
      <PageTabs tabsData={tabsData} />
      <Outlet />
    </div>
  )
};

export default Subscription;
